import type { JSONContent } from "@tiptap/core"
import type { StudioQualityValue } from "@/components/studio-quality-mark"
import { defaultParamsFor } from "@/lib/canvas-model-defaults"
import type { TimelineDocument } from "@/lib/timeline"

export type CanvasComposerState = {
  media_kind?: CanvasMediaKind
  aspect_ratio?: string | null
  duration_sec?: number | null
  use_project_styles?: boolean
  prompt_text_by_kind?: Partial<Record<CanvasMediaKind, string>>
  model_id_by_kind?: Partial<Record<CanvasMediaKind, string>>
  reference_items_by_kind?: Partial<Record<CanvasMediaKind, CanvasReferenceItem[]>>
  /** Draft quality selects only (`resolution` / `image_size`), per media kind. */
  model_params_by_kind?: Partial<Record<CanvasMediaKind, Record<string, string>>>
}

export type CanvasBoardRow = {
  id: number
  number: number
  title: string
  image_model_id?: string | null
  video_model_id?: string | null
  composer_state?: CanvasComposerState
  /** Сколько загрузок держит доска — показывается в подтверждении удаления. */
  upload_count?: number
  /** Сколько генераций на доске. Крестик удаления только при 0. */
  generation_count?: number
}

export type CanvasMediaKind = "image" | "video"
export type CanvasStatus = "queued" | "processing" | "completed" | "failed"
export type CanvasFeedScope = number | "all"
export type CanvasView = "board" | "all"

export type CanvasModelExtraParam = {
  key: string
  kind: string
  options: string[]
  default: string
}

export type CanvasModel = {
  id: string
  provider: string
  label: string
  media_kind: CanvasMediaKind
  aspect_ratios: string[]
  durations: number[]
  extra_params: CanvasModelExtraParam[]
  enabled: boolean
  /** True when the platform provider connection is turned off. */
  platform_disabled?: boolean
  /** True when the company allowlist excludes this model */
  restricted?: boolean
  wire?: string
  /** Per-file video-reference max duration in seconds for the current model. */
  max_video_ref_seconds?: number
  /** Combined video-reference max duration in seconds for the current model. */
  max_total_video_ref_seconds?: number
  /** Max audio reference count when the model supports reference_audio (Seedance). */
  max_audio_refs?: number | null
  /** Per-file audio-reference max duration in seconds. */
  max_audio_ref_seconds?: number | null
  /** Combined audio-reference max duration in seconds. */
  max_total_audio_ref_seconds?: number | null
}

export type CanvasDefaults = {
  image_model_id: string
  video_model_id: string
  frame_format: string
  /** Delivery FPS for timeline trim/timecode (company: jobs host; project may omit — use project prop). */
  effective_video_output_fps?: number
}

export type CanvasReferenceItem = {
  kind: string
  label: string
  /** Display / lightbox URL того же кадра, что в тайле (не thumb). Attach у character — см. fetch_url. */
  url: string
  /** Full original same-origin proxy for sketch / fetch (not display ≤800). */
  download_url?: string | null
  media_type: "image" | "video" | "audio"
  entity_type?: string | null
  entity_id?: number | string | null
  /** Состояние сущности: id стейта пропа (number) или "first"|"last" для shot_version_frame. */
  state_id?: number | string | null
  /** Attach/download, если отличается от url (character: sheet/ref вместо portrait). */
  fetch_url?: string | null
  /** Миниатюра для стрипа / сетки пикера (image). */
  thumb_url?: string | null
  /** Blob оригинала для attach-by-reference (`project_asset`), тот же файл что `download_url`. */
  blob_id?: number | null
  /** Seconds from blob metadata when this ref is a video/audio file. */
  duration_seconds?: number | null
  /** Локально: signed_id загруженного файла (передаётся при создании генерации). */
  signed_id?: string | null
  /**
   * ТОЛЬКО локально: файл загружен в этой сессии и генерация с ним ещё не ушла — крестик
   * отменяет загрузку на сервере. Гаснет после успешной генерации (Canvas.tsx). Отдельный
   * флаг нужен потому, что различить загрузку и выбор из Библиотеки больше нечем:
   * entity_type/entity_id у них намеренно совпадают ради дедупа, а kind уезжает на сервер
   * и возвращается при «Перегенерировать» уже без signed_id.
   * В reference_items не попадает: createCanvasGeneration шлёт явный белый список полей.
   */
  cancellable_upload?: boolean
  /**
   * Для entity character: можно ли прикрепить (есть sheet/ref или portrait в fetch_url).
   * Тайл/lightbox — portrait (url + thumb_url); attach — sheet/ref через fetch_url, иначе portrait.
   */
  attachable?: boolean
}

export type CanvasSavedTo = {
  kind: "character" | "location" | "prop" | "shot_storyboard" | "shot_shooting_board" | "shot_video"
  id: number
  project_id?: number
  label: string
  preview_url?: string | null
  facing?: string | null
}

export type CanvasGenerationRow = {
  id: number
  number: number
  canvas_id?: number
  canvas_number?: number | null
  canvas_title?: string | null
  media_kind: CanvasMediaKind
  status: CanvasStatus
  provider: string | null
  model_id: string
  model_label: string
  aspect_ratio: string
  duration_sec: number | null
  prompt_text: string
  display_prompt: string
  prompt_doc: JSONContent | null
  use_project_styles: boolean
  model_params: Record<string, unknown>
  reference_items: CanvasReferenceItem[]
  width: number | null
  height: number | null
  duration_ms: number | null
  editor_timeline?: TimelineDocument | Record<string, never>
  error_message: string | null
  error_kind?: string | null
  media_url: string | null
  media_thumb_url?: string | null
  download_url: string | null
  first_frame_url?: string | null
  first_frame_thumb_url?: string | null
  /** Кадры filmstrip (~1 fps). Пусто, пока полоса не нарезана — тогда лента берёт first_frame_url. */
  timeline_frame_urls?: string[]
  content_type: string | null
  saved_to?: CanvasSavedTo | null
  studio_quality?: StudioQualityValue
  draft_finalize?: unknown
  created_at: string
  updated_at: string
}

export type CanvasGenerationsPage = {
  generations: CanvasGenerationRow[]
  next_cursor: number | null
  has_more: boolean
  failed_count?: number
}

export type CanvasShotTreeShot = {
  id: number
  number: string
  position: number
  storyboard_url?: string | null
  shooting_board_url?: string | null
  video_first_frame_url?: string | null
  /** Миниатюра ~200 для меню. Не display ≤800. */
  thumb_url?: string | null
}
export type CanvasShotTreeScene = { id: number; number: number; title: string | null; shots: CanvasShotTreeShot[] }
export type CanvasShotTreeEpisode = {
  id: number
  number: number
  title: string | null
  scenes: CanvasShotTreeScene[]
}

export type CanvasProjectStyles = {
  styles_enabled?: boolean
  visual_rendering_style: string
  genre: string
  tone: string
}

/** Черновик настроек панели генерации (то, что отправляется на бэкенд). */
export type CanvasDraft = {
  mediaKind: CanvasMediaKind
  modelId: string
  modelIdByKind: Record<CanvasMediaKind, string>
  aspectRatio: string
  durationSec: number | null
  /** Separate prompts per media kind — image edits must not overwrite video and vice versa. */
  promptTextByKind: Record<CanvasMediaKind, string>
  promptDocByKind: Record<CanvasMediaKind, JSONContent | null>
  useProjectStyles: boolean
  modelParams: Record<string, string>
  /**
   * Persisted quality selects (`resolution` / `image_size`) per media kind so image/video
   * do not overwrite each other across tab switches and reloads.
   */
  qualityParamsByKind: Record<CanvasMediaKind, Record<string, string>>
  /**
   * Canvas video: attach character scale sheet + height lines when ≥2 hero refs.
   * Sent as model_params.video_consider_scale; preserved across model switches.
   */
  videoConsiderScale: boolean
  /** Separate reference strips per media kind — image edits must not overwrite video and vice versa. */
  referencesByKind: Record<CanvasMediaKind, CanvasReferenceItem[]>
}

/** Default true when key missing (shot parity). */
export function videoConsiderScaleFromParams(params: Record<string, unknown> | null | undefined): boolean {
  if (params == null || !Object.prototype.hasOwnProperty.call(params, "video_consider_scale")) return true
  const raw = params.video_consider_scale
  if (typeof raw === "boolean") return raw
  return String(raw).toLowerCase() === "true" || String(raw) === "1"
}

export function canvasActivePromptText(draft: CanvasDraft): string {
  return draft.promptTextByKind[draft.mediaKind] ?? ""
}

export function canvasActivePromptDoc(draft: CanvasDraft): JSONContent | null {
  return draft.promptDocByKind[draft.mediaKind] ?? null
}

export function canvasActiveReferences(draft: CanvasDraft): CanvasReferenceItem[] {
  return draft.referencesByKind[draft.mediaKind] ?? []
}

/** Whitelist for generate + composer_state persist (no signed_id / cancellable_upload). */
export function canvasPersistableReferenceItem(ref: CanvasReferenceItem): CanvasReferenceItem {
  return {
    kind: ref.kind,
    label: ref.label,
    url: ref.url,
    download_url: ref.download_url ?? null,
    thumb_url: ref.thumb_url ?? null,
    media_type: ref.media_type === "video" ? "video" : ref.media_type === "audio" ? "audio" : "image",
    entity_type: ref.entity_type ?? null,
    entity_id: ref.entity_id ?? null,
    state_id: ref.state_id ?? null,
    ...(typeof ref.duration_seconds === "number" && Number.isFinite(ref.duration_seconds) && ref.duration_seconds > 0
      ? { duration_seconds: ref.duration_seconds }
      : {}),
  }
}

export function canvasPersistableReferencesByKind(
  refs: Record<CanvasMediaKind, CanvasReferenceItem[]>,
): Record<CanvasMediaKind, CanvasReferenceItem[]> {
  return {
    image: (refs.image ?? []).map(canvasPersistableReferenceItem),
    video: (refs.video ?? []).map(canvasPersistableReferenceItem),
  }
}

export function canvasReferencesFromComposerState(
  byKind: CanvasComposerState["reference_items_by_kind"] | undefined,
): Record<CanvasMediaKind, CanvasReferenceItem[]> {
  return {
    image: (byKind?.image ?? []).map(canvasPersistableReferenceItem),
    video: (byKind?.video ?? []).map(canvasPersistableReferenceItem),
  }
}

const CANVAS_QUALITY_PARAM_KEYS = ["resolution", "image_size"] as const

export function canvasQualityParamsFrom(
  modelParams: Record<string, string> | null | undefined,
): Record<string, string> {
  const out: Record<string, string> = {}
  if (!modelParams) return out
  for (const key of CANVAS_QUALITY_PARAM_KEYS) {
    const value = modelParams[key]
    if (typeof value === "string" && value.trim() !== "") out[key] = value.trim()
  }
  return out
}

export function canvasQualityParamsByKindFromComposerState(
  byKind: CanvasComposerState["model_params_by_kind"] | undefined,
): Record<CanvasMediaKind, Record<string, string>> {
  return {
    image: canvasQualityParamsFrom(byKind?.image),
    video: canvasQualityParamsFrom(byKind?.video),
  }
}

/** Defaults for model + overlay saved quality keys only when still valid for that model. */
export function canvasMergeQualityParams(
  model: CanvasModel | null | undefined,
  saved: Record<string, string> | null | undefined,
): Record<string, string> {
  const base = defaultParamsFor(model)
  if (!model || !saved) return base
  const out = { ...base }
  for (const key of CANVAS_QUALITY_PARAM_KEYS) {
    const value = saved[key]
    if (typeof value !== "string" || value.trim() === "") continue
    const param = model.extra_params.find((p) => p.key === key)
    if (!param || !param.options.includes(value)) continue
    out[key] = value
  }
  return out
}
